import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, ChevronsUpDown, Loader2, UserSearch } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

export const AllDeclinedDriverRecovery = ({ api, candidateId, disabled, onResolved }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!candidateId) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get(`/fleet/driver-candidates/${candidateId}/eligible-people`, { params: { search, limit: 50 } });
        if (active) setPeople(response.data?.people || []);
      } catch (error) {
        if (active) toast.error(error?.response?.data?.detail || 'Could not load eligible people');
      } finally {
        if (active) setLoading(false);
      }
    }, search ? 250 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [api, candidateId, search]);

  const assign = async () => {
    if (!selected || !reason.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/fleet/driver-candidates/${candidateId}/resolve-directory`, {
        person_id: selected.person_id,
        person_type: selected.person_type,
        reason: reason.trim(),
      });
      toast.success(`Driver assigned: ${selected.person_name}`);
      onResolved?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not assign this person');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-sky-300 pt-3" data-testid="driver-directory-recovery-panel">
      <div className="flex gap-2 rounded-md bg-sky-50 p-2.5 text-xs text-sky-950 dark:bg-sky-950/40 dark:text-sky-100" data-testid="driver-directory-recovery-warning">
        <UserSearch className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Beacon not seen? Search active Drivers and Staff Eligible to Drive. This assignment will be audited as having no BLE evidence.</p>
      </div>
      <div className="space-y-1.5">
        <Label>Choose from People</Label>
        <Popover modal={true} open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="w-full justify-between" disabled={disabled || submitting} data-testid="driver-directory-person-trigger">
              <span className="truncate">{selected?.person_name || 'Search eligible people'}</span>
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="z-[10000] w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            sideOffset={6}
            collisionPadding={12}
            data-testid="driver-directory-person-popover"
          >
            <Command shouldFilter={false} data-testid="driver-directory-person-menu">
              <CommandInput value={search} onValueChange={setSearch} placeholder="Search Drivers or eligible Staff" data-testid="driver-directory-person-search" />
              <CommandList>
                <CommandEmpty data-testid="driver-directory-empty-state">{loading ? 'Searching…' : 'No eligible people found.'}</CommandEmpty>
                <CommandGroup>
                  {people.map((person) => (
                    <CommandItem
                      key={`${person.person_type}-${person.person_id}`}
                      value={`${person.person_name}-${person.person_id}`}
                      onSelect={() => { setSelected(person); setOpen(false); }}
                      data-testid={`driver-directory-person-${person.person_type}-${person.person_id}`}
                    >
                      <Check className={`h-4 w-4 ${selected?.person_id === person.person_id ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="min-w-0 flex-1 truncate">{person.person_name}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">{person.person_type}</Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      {selected && (
        <div className="space-y-1.5" data-testid="driver-directory-selected-person">
          <Label htmlFor="driver-directory-reason">Assignment reason</Label>
          <Textarea id="driver-directory-reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="How was this Driver confirmed despite no BLE detection?" data-testid="driver-directory-reason" />
          <Button className="w-full" onClick={assign} disabled={disabled || submitting || !reason.trim()} data-testid="driver-directory-assign-button">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
            Assign without BLE evidence
          </Button>
        </div>
      )}
    </div>
  );
};